import { useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { fr } from "date-fns/locale";
import { usePerformance } from "../context/PerformanceContext";
import { COLORS } from "../../../utils/colors";

interface PerformanceFilterPanelProps {
  onSelectClick: () => void;
}

const PerformanceFilterPanel: React.FC<PerformanceFilterPanelProps> = ({
  onSelectClick,
}) => {
  const { t } = useTranslation();
  const analysisPickerRef = useRef<any>(null);
  const comparativePickerRef = useRef<any>(null);
  const {
    view,
    performanceRange,
    setPerformanceRange,
    analysisDate,
    setAnalysisDate,
    comparativeDate,
    setComparativeDate,
    derivedComparativeDate,
    selectedMagasin,
    resetFilters,
    isCALive,
    isPerformanceLoading,
  } = usePerformance();

  useEffect(() => {
    if (performanceRange !== "CUSTOM" && derivedComparativeDate) {
      setComparativeDate(derivedComparativeDate);
    }
  }, [derivedComparativeDate, performanceRange, setComparativeDate]);


  const isSameDate = (first: Date | null, second: Date | null) =>
    !!first &&
    !!second &&
    first.toDateString() === second.toDateString();

  const handleSetPerformanceRange = (range: string) => {
    if (isPerformanceLoading) return;
    if (range === performanceRange) return;
    if (range === "CUSTOM" && !analysisDate && view !== "horaire") {
      setAnalysisDate(new Date());
    }
    setPerformanceRange(range);
  };

  const handleAnalysisDateChange = (value: Date | null) => {
      if (isPerformanceLoading) return;
      if (!value || isNaN(value.getTime()) || value > new Date()) {
        return;
      }
      if (isSameDate(analysisDate, value)) {
        analysisPickerRef.current?.setOpen(false);
        analysisPickerRef.current?.setBlur();
        return;
      }
      setAnalysisDate(value);
      analysisPickerRef.current?.setOpen(false);
      analysisPickerRef.current?.setBlur();
  };

  const handleCustComparativeDateChange = (value: Date | null) => {
      if (isPerformanceLoading) return;
      if (!value || isNaN(value.getTime()) || value > new Date()) {
        return;
      }
      if (!analysisDate) {
        setAnalysisDate(new Date());
      }
      if (isSameDate(comparativeDate, value)) {
        comparativePickerRef.current?.setOpen(false);
        comparativePickerRef.current?.setBlur();
        return;
      }
      setComparativeDate(value);
      comparativePickerRef.current?.setOpen(false);
      comparativePickerRef.current?.setBlur();
  };

  if(isCALive){
    return null;
  }

  return (
    <FilterPanel>
      <FilterFieldWrapper> 
        <FilterField style={{ marginLeft: 0 }}>
          <FilterLabel>Date d'analyse</FilterLabel>
          <DateFieldWrapper>
            <DateInputContainer
              $disabled={isPerformanceLoading}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (isPerformanceLoading) {
                  return;
                }
                if (target.closest(".react-datepicker")) {
                  return;
                }
                analysisPickerRef.current?.setOpen(true);
              }}
            >
              <DatePicker
                    ref={analysisPickerRef}
                    selected={analysisDate || new Date()}
                    onChange={(date: Date | null) => handleAnalysisDateChange(date)}
                    dateFormat="dd/MM/yyyy"
                    locale={fr}
                    maxDate={new Date()}
                    disabled={isPerformanceLoading}
                    preventOpenOnFocus={true}
                    customInput={<input readOnly disabled={isPerformanceLoading} style={styles.input} />}
              />
              <mrcl-icon icon="calendar" size="m" style={{ color: isPerformanceLoading ? "#94a3b8" : COLORS.blue, fontSize: 24 }} />
            </DateInputContainer>
          </DateFieldWrapper>
        </FilterField>
        <ButtonsContainer>
          <mrcl-chip
              size="s"
              selected={performanceRange === "N-1"}
              disabled={isPerformanceLoading}
              onClick={() => handleSetPerformanceRange('N-1')}>
              N-1
            </mrcl-chip>

            <mrcl-chip
              size="s"
              selected={performanceRange === "S-1"}
              disabled={isPerformanceLoading}
              onClick={() => handleSetPerformanceRange('S-1')}
            >
              S-1
            </mrcl-chip>

            <mrcl-chip
              size="s"
              selected={performanceRange === "CUSTOM"}
              disabled={isPerformanceLoading}
              onClick={() => handleSetPerformanceRange('CUSTOM')}
            >
              Manuel
            </mrcl-chip>
        </ButtonsContainer>
        <FilterField style={{ marginLeft: 0 }}>
          <FilterLabel>Date comparative</FilterLabel>
          <DateFieldWrapper>
            <DateInputContainer
              $disabled={performanceRange !== "CUSTOM" || isPerformanceLoading}
              style={{
                ...((performanceRange !== "CUSTOM" || isPerformanceLoading) ? { background: "#f1f5f9", cursor: "not-allowed" } : {})
              }}
              onClick={(e) => {
                if (performanceRange !== "CUSTOM" || isPerformanceLoading) return;
                const target = e.target as HTMLElement;
                if (target.closest(".react-datepicker")) {
                  return;
                }
                comparativePickerRef.current?.setOpen(true);
              }}
            >
              <DatePicker
                    ref={comparativePickerRef}
                    selected={comparativeDate ?? derivedComparativeDate}
                    onChange={(date: Date | null) => handleCustComparativeDateChange(date)}
                    dateFormat="dd/MM/yyyy"
                    locale={fr}
                    disabled={performanceRange !== "CUSTOM" || isPerformanceLoading}
                    maxDate={(() => {
                      const date = analysisDate ? new Date(analysisDate) : new Date();
                      date.setDate(date.getDate() - 1);
                      return date;
                    })()}
                    preventOpenOnFocus={true}
                    customInput={<input readOnly disabled={isPerformanceLoading} style={styles.input} />}
              />
              <mrcl-icon icon="calendar" size="m" style={{ color: performanceRange === "CUSTOM" && !isPerformanceLoading ? COLORS.blue : "#94a3b8", fontSize: 24 }} />
            </DateInputContainer>
          </DateFieldWrapper>
        </FilterField>
        {view === "horaire" && (
          <>
            <FilterBar style={{ marginLeft: "auto" }} />
            <StoreFilterContainer>
              <FilterField style={{ marginLeft: 0, width: "100%" }}>
                <FilterLabel>{t("commercial.magasin_comparer")}</FilterLabel>
                <ModalTrigger type="button" onClick={onSelectClick} disabled={isPerformanceLoading}>
                  <span>{selectedMagasin ? selectedMagasin.label : t("commercial.selectionner")}</span>
                  <StyledSvg $selected={!!selectedMagasin} viewBox="0 0 24 24">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                  </StyledSvg>
                </ModalTrigger>
              </FilterField>
              <ResetButton type="button" onClick={resetFilters} disabled={isPerformanceLoading}>
                <mrcl-icon icon="refresh" size="m" />
                {t("commercial.reinitialiser")}
              </ResetButton>
            </StoreFilterContainer>
          </>
        )}
      </FilterFieldWrapper>
    </FilterPanel>
  );
};

export default PerformanceFilterPanel;

const FilterPanel = styled.div`
  border-top: 1px solid #e2e8f0;
  padding: 16px 20px;
  background: #F5FAFF;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  animation: slideDown 0.2s ease;
 
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    padding: 10px 12px;
  }
`;

const FilterFieldWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 16px;
  flex-wrap: wrap;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    gap: 8px;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: auto;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    gap: 4px;
    flex: 1;
    min-width: 0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 90px;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    gap: 4px;
    height: auto;
    margin-top: 20px;
    flex-shrink: 0;
  }
`;

const FilterBar = styled.div`
  width: 1px;
  height: 46px;
  border: 1px solid #D9D9D9;
  flex-shrink: 0;
  align-self: center;

  @media (max-width: 1024px) {
    border: none;
  }

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    display: block;
    width: 100%;
    height: 0;
    border: none;
    border-top: 1px solid #D9D9D9;
    margin: 6px 0 0 0 !important;
  }
`;

const FilterLabel = styled.label`
  font-family: Ubuntu, system-ui, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: var(--font-size-body-s, 14px);
  line-height: var(--line-height-body-s, 20px);
  letter-spacing: var(--letter-spacing-normal, normal);
  vertical-align: middle;
  color: #454545;
  white-space: nowrap;
  text-align: left;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const ModalTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 14px;
  background: #ffffff;
  font-size: 16px;
  font-family: Ubuntu, system-ui, sans-serif;
  color: #121212;
  cursor: pointer;
  transition: border-color 0.15s ease;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    height: 36px;
    padding: 0 8px;
    font-size: 13px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
 
  &:hover {
    border-color: #2563eb;
    color: #121212;
  }

  &:disabled {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;


const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #2563eb;
  font-size: 16px;
  font-weight: 700;
  font-family: Ubuntu, system-ui, sans-serif;
  cursor: pointer;
  padding: 0;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    font-size: 12px;
    height: 36px;
    white-space: nowrap;
  }

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const StoreFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 311px;
  max-width: 100%;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;

    ${FilterField} {
      width: 311px !important;
      max-width: 100%;
      flex: none;
    }
  }
`;

const DateFieldWrapper = styled.div`
  position: relative;
  width: 100%;

  .react-datepicker-popper {
    transform: none !important;
    position: absolute !important;
    inset: auto !important;
    top: calc(100% + 2px) !important;
    left: 0 !important;
    width: 150px;
    @media (min-width: 900px) {
      width: 330px;
    }

  }
  .react-datepicker,
  .react-datepicker__month-container {
    width: 100%;
    box-sizing: border-box;
  }
  .react-datepicker__day-names,
  .react-datepicker__week {
    display: flex;
    justify-content: space-around;
  }
`;

const StyledSvg = styled.svg<{ $selected: boolean }>`
  width: 24px;
  height: 24px;
  fill: none;
  stroke: ${({ $selected }) => ($selected ? "#2563eb" : "#121212")};
  stroke-width: 2px;
  transform: rotate(90deg);
  flex-shrink: 0;

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    width: 20px;
    height: 20px;
  }
`;
const DateInputContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #cfcfcf;
  border-radius: 4px;
  background: ${({ $disabled }) => ($disabled ? "#f1f5f9" : "#fff")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  padding: 0 12px;
  height: 40px;
  width: 150px;
  box-sizing: border-box;

  .react-datepicker-wrapper {
    flex: 1;
    min-width: 0;
  }

  @media (min-width: 1000px) {
    width: 330px;
  }

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    width: 100%;
    height: 36px;
    padding: 0 8px;

    input {
      font-size: 13px !important;
    }
  }
`;
const styles: Record<string, React.CSSProperties> = {
  periodButton: {
    height: 36,
    padding: "0 18px",
    borderRadius: 18,
    border: "1px solid #bbb",
    background: "#fff",
    color: "#000",
    cursor: "pointer",
    fontWeight: 500,
  },

    dateInput: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #cfcfcf",
    borderRadius: 4,
    background: "#fff",
    padding: "0 12px",
    height: 40,
    width: 330,
  },

  input: {
    border: "none",
    outline: "none",
    fontSize: 16,
    width: "100%",
    background: "transparent",
    caretColor: "transparent",
  },

};
